export type DeadlineWindowFilter =
  | 'all'
  | 'overdue'
  | 'today'
  | '3'
  | '7'
  | '15'
  | 'unscheduled';

export type OperationalDeadlineLike = {
  dueDate:
    string |
    null |
    undefined;

  status:
    string;

  riskLevel:
    string;
};

export type DeadlineTimingState =
  | 'closed'
  | 'unscheduled'
  | 'overdue'
  | 'today'
  | 'upcoming'
  | 'future';

export type DeadlineTiming = {
  state:
    DeadlineTimingState;

  daysUntil:
    number |
    null;
};

export type CaseDeadlineOverviewSummary = {
  active:
    number;

  overdue:
    number;

  today:
    number;

  next3:
    number;

  next7:
    number;

  next15:
    number;

  unscheduled:
    number;

  highRisk:
    number;
};

export const isOperationalDeadlineActive =
  (
    status:
      string
  ) =>
    status ===
      'pending' ||
    status ===
      'in_progress';

const parseDateOnlyUtc =
  (
    value:
      string
  ) => {
    const match =
      /^(\d{4})-(\d{2})-(\d{2})$/.exec(
        value
      );

    if (!match) {
      return null;
    }

    const year =
      Number(
        match[1]
      );

    const month =
      Number(
        match[2]
      );

    const day =
      Number(
        match[3]
      );

    const date =
      new Date(
        Date.UTC(
          year,
          month - 1,
          day
        )
      );

    if (
      date.getUTCFullYear() !==
        year ||
      date.getUTCMonth() !==
        month - 1 ||
      date.getUTCDate() !==
        day
    ) {
      return null;
    }

    return date;
  };

export const getLocalDateOnly =
  (
    date =
      new Date()
  ) => {
    const year =
      date.getFullYear();

    const month =
      String(
        date.getMonth() +
        1
      ).padStart(
        2,
        '0'
      );

    const day =
      String(
        date.getDate()
      ).padStart(
        2,
        '0'
      );

    return `${year}-${month}-${day}`;
  };

export const classifyOperationalDeadline =
  (
    deadline:
      OperationalDeadlineLike,

    today:
      string
  ):
    DeadlineTiming => {
    if (
      !isOperationalDeadlineActive(
        deadline.status
      )
    ) {
      return {
        state:
          'closed',

        daysUntil:
          null,
      };
    }

    const dueDate =
      String(
        deadline.dueDate ??
        ''
      ).trim();

    if (!dueDate) {
      return {
        state:
          'unscheduled',

        daysUntil:
          null,
      };
    }

    const parsedDue =
      parseDateOnlyUtc(
        dueDate
      );

    const parsedToday =
      parseDateOnlyUtc(
        today
      );

    if (
      !parsedDue ||
      !parsedToday
    ) {
      return {
        state:
          'unscheduled',

        daysUntil:
          null,
      };
    }

    const daysUntil =
      Math.round(
        (
          parsedDue.getTime() -
          parsedToday.getTime()
        ) /
        86400000
      );

    if (
      daysUntil <
      0
    ) {
      return {
        state:
          'overdue',

        daysUntil,
      };
    }

    if (
      daysUntil ===
      0
    ) {
      return {
        state:
          'today',

        daysUntil:
          0,
      };
    }

    if (
      daysUntil <=
      15
    ) {
      return {
        state:
          'upcoming',

        daysUntil,
      };
    }

    return {
      state:
        'future',

      daysUntil,
    };
  };

export const buildCaseDeadlineOverviewSummary =
  (
    deadlines:
      OperationalDeadlineLike[],

    today:
      string
  ):
    CaseDeadlineOverviewSummary => {
    const summary:
      CaseDeadlineOverviewSummary = {
      active:
        0,

      overdue:
        0,

      today:
        0,

      next3:
        0,

      next7:
        0,

      next15:
        0,

      unscheduled:
        0,

      highRisk:
        0,
    };

    deadlines.forEach(
      (
        deadline
      ) => {
        if (
          !isOperationalDeadlineActive(
            deadline.status
          )
        ) {
          return;
        }

        summary.active +=
          1;

        if (
          deadline.riskLevel ===
          'high'
        ) {
          summary.highRisk +=
            1;
        }

        const timing =
          classifyOperationalDeadline(
            deadline,
            today
          );

        if (
          timing.state ===
          'overdue'
        ) {
          summary.overdue +=
            1;

          return;
        }

        if (
          timing.state ===
          'today'
        ) {
          summary.today +=
            1;

          return;
        }

        if (
          timing.state ===
          'unscheduled'
        ) {
          summary.unscheduled +=
            1;

          return;
        }

        if (
          timing.daysUntil !==
            null &&
          timing.daysUntil >
            0
        ) {
          if (
            timing.daysUntil <=
            3
          ) {
            summary.next3 +=
              1;
          }

          if (
            timing.daysUntil <=
            7
          ) {
            summary.next7 +=
              1;
          }

          if (
            timing.daysUntil <=
            15
          ) {
            summary.next15 +=
              1;
          }
        }
      }
    );

    return summary;
  };

export const matchesDeadlineWindow =
  (
    deadline:
      OperationalDeadlineLike,

    today:
      string,

    filter:
      DeadlineWindowFilter
  ) => {
    if (
      !isOperationalDeadlineActive(
        deadline.status
      )
    ) {
      return false;
    }

    if (
      filter ===
      'all'
    ) {
      return true;
    }

    const timing =
      classifyOperationalDeadline(
        deadline,
        today
      );

    if (
      filter ===
      'overdue'
    ) {
      return timing.state ===
        'overdue';
    }

    if (
      filter ===
      'today'
    ) {
      return timing.state ===
        'today';
    }

    if (
      filter ===
      'unscheduled'
    ) {
      return timing.state ===
        'unscheduled';
    }

    if (
      timing.daysUntil ===
        null ||
      timing.daysUntil <=
        0
    ) {
      return false;
    }

    const limit =
      Number(
        filter
      );

    return (
      Number.isFinite(
        limit
      ) &&
      timing.daysUntil <=
        limit
    );
  };

const riskRank:
  Record<
    string,
    number
  > = {
  high:
    0,

  medium:
    1,

  low:
    2,
};

export const compareOperationalDeadlines =
  (
    left:
      OperationalDeadlineLike,

    right:
      OperationalDeadlineLike,

    today:
      string
  ) => {
    const leftTiming =
      classifyOperationalDeadline(
        left,
        today
      );

    const rightTiming =
      classifyOperationalDeadline(
        right,
        today
      );

    const leftDays =
      leftTiming.daysUntil ??
      Number.MAX_SAFE_INTEGER;

    const rightDays =
      rightTiming.daysUntil ??
      Number.MAX_SAFE_INTEGER;

    if (
      leftDays !==
      rightDays
    ) {
      return leftDays -
        rightDays;
    }

    return (
      (
        riskRank[
          left.riskLevel
        ] ??
        9
      ) -
      (
        riskRank[
          right.riskLevel
        ] ??
        9
      )
    );
  };